import React, { memo } from "react";
import { motion } from "framer-motion";
import { Task } from "./taskTypes";
import { formatTime } from "./utils";
import { TaskStatusIndicator } from "./TaskStatusIndicator";
import { TaskProgressDetails } from "./TaskProgressDetails";
import { SearchProgressMetrics } from "@/modules/snapshots/grid/sidebar/search/useSearchProgress";

export interface Props {
  task: Task;
  status: "pending" | "active" | "completed";
  metrics: SearchProgressMetrics;
  thinking?: string;
  relevantFields?: string[];
  documents?: number;
}

export const TaskProgressItem: React.FC<Props> = memo(
  ({ task, status, metrics, thinking, relevantFields, documents }) => (
    <motion.div
      key={task.id}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: task.order * 0.08 }}
      className="flex flex-col gap-1"
    >
      {/* Task Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
            <TaskStatusIndicator status={status} />
          </div>
          <span
            className={`truncate text-sm font-medium ${
              status === "completed" ? "text-gray-600" : status === "active" ? "text-gray-700" : "text-gray-600"
            }`}
          >
            {task.label}
          </span>
        </div>

        {/* Time Indicators */}
        {status === "active" && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="shrink-0 font-mono text-xs text-gray-600"
          >
            {formatTime(metrics.elapsedTime)}
          </motion.span>
        )}
        {status === "completed" &&
          (metrics.phaseTimings[task.id as keyof typeof metrics.phaseTimings] || task.fakeDuration) && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="shrink-0 font-mono text-xs text-gray-600"
            >
              {formatTime(
                metrics.phaseTimings[task.id as keyof typeof metrics.phaseTimings]?.duration || task.fakeDuration || 0,
              )}
            </motion.span>
          )}
      </div>

      {/* Progress Bar for Active Task */}
      {status === "active" && (metrics.phase === "retrieving" || metrics.phase === "analyzing") && (
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.3 }}
          className="h-1 w-full origin-left overflow-hidden rounded-full bg-gray-200"
        >
          <motion.div
            className="h-full bg-black"
            initial={{ width: "0%" }}
            animate={{ width: `${metrics.progressPercentage}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </motion.div>
      )}

      {/* Task Progress Details */}
      <TaskProgressDetails
        task={task}
        status={status}
        metrics={metrics}
        thinking={thinking}
        relevantFields={relevantFields}
        documents={documents}
      />
    </motion.div>
  ),
);
